import React, { useState, useEffect } from 'react';
import { Modal, Checkbox, Radio, Tooltip, Upload } from 'antd';
import { Button, FormInput, FormSelect, FormSubmitButton, MasterDetailForm, Widget, Row, FormTextArea } from '@digital/ui';
import { UploadFile, Visibility } from '@app/components';
import { executarAcaoFornecedor, downloadTemplateEsclarecimentoTecnico, viewTemplateEsclarecimentoTecnico } from '@app/api/sas/sasFornecedor';
import { downloadArquivo, viewArquivo, IAcaoFornecedor } from '@app/api/sas/sasDocumentoOrdemCompra';
import CurrencyInput from '@app/components/CurrencyInput';
import { GetSasConsicaoPagamentoOptions, getComentariosNegociacaoMinuta } from '@app/api/sas/sasProposta';
import { IUploadFile } from '@app/types/core.types';
import * as Yup from 'yup';
import { isObjNotEmpty } from '@app/helpers/objHelper';
import ModalExibicaoArquivos from '../WidgetDocumentacao/ModalExibicaoArquivos';
import ListMensagem from '../WidgetAcompanhamentoProcesso/ComentariosParecerTecnico/ListMensagem';
import { useRecoilState } from 'recoil';
import { ordemCompraIdState } from '../atoms';

interface Props {
  visible: boolean;
  acao: IAcaoFornecedor;
  ordemCompraId: string;
  onClose: () => void;
  onSuccess: () => void;
  readOnly?: boolean;
}

const ModalAcaoFornecedor = ({ visible, acao, ordemCompraId, onClose, onSuccess, readOnly = false }: Props) => {
  const [ordemCompra, setOrdemCompraId] = useRecoilState(ordemCompraIdState);
  const [confirmado, setConfirmado] = useState(false);
  const [decisao, setDecisao] = useState<'aceitar' | 'recusar'>('aceitar');
  const [fileList, setFileList] = useState<IUploadFile[]>([]);
  const [condicoesPagamento, setCondicoesPagamento] = useState<{ text: string; value: string }[]>([]);
  const [mensagensMinuta, setMensagensMinuta] = useState([]);

  setOrdemCompraId(ordemCompraId)

  useEffect(() => {
    if (acao.requisitos.requerCondicaoPagamento) {
      GetSasConsicaoPagamentoOptions().then(setCondicoesPagamento);
    }
  }, [acao.requisitos.requerCondicaoPagamento]);

  useEffect(() => {
    if (visible && acao.tipoAcao === 'negociar_minuta_contratual') {
      getComentariosNegociacaoMinuta(acao.id).then(setMensagensMinuta);
    }
  }, [visible, acao.tipoAcao, acao.id]);

  const handleSubmit = (values: any) => {
    const executar = () => {
      const formData = new FormData();

      formData.append('id', acao.id);
      formData.append('ordemCompraId', ordemCompraId);
      formData.append('tipoAcao', acao.tipoAcao);
      formData.append('decisao', decisao);

      if (acao.requisitos.requerJustificativa && values.justificativa) {
        formData.append('justificativa', values.justificativa);
      }

      if (acao.requisitos.requerValor) formData.append('valor', String(parseFloat(values.valor)).replace('.', ','));

      if (acao.requisitos.requerCondicaoPagamento && values.sasCondicaoPagamentoId)
        formData.append('sasCondicaoPagamentoId', values.sasCondicaoPagamentoId);

      if (acao.requisitos.requerMultiplosArquivos) {
        fileList.forEach((file: any, index: number) => {
          if (file.originFileObj) {
            formData.append('arquivos', file.originFileObj);
          }
        });
      } else if (acao.requisitos.requerUpload) {
        formData.append('arquivo', values.arquivo);
      }

      const deveRequerUploadQuandoRecusado =
        acao.requisitos.requerUploadQuandoRecusado &&
        acao.requisitos.aceitarOuRecusar &&
        decisao === 'recusar';

      const deveRequerUploadQuandoAceitar =
        acao.requisitos.requerUploadQuandoAceitar &&
        acao.requisitos.aceitarOuRecusar &&
        decisao === 'aceitar';

      if (deveRequerUploadQuandoRecusado && !acao.requisitos.requerMultiplosArquivos) {
        formData.append('arquivo', values.arquivo);
      }

      if (deveRequerUploadQuandoAceitar && !acao.requisitos.requerMultiplosArquivos) {
        formData.append('arquivo', values.arquivo);
      }

      if (acao.requisitos.requerMultiplosArquivos && fileList.length === 0) {
        Modal.error({
          title: 'Erro ao enviar ação',
          content: 'É obrigatório o envio de pelo menos um arquivo para prosseguir com a ação.',
        });
        return;
      }

      if (acao.requisitos.requerUpload && !acao.requisitos.requerMultiplosArquivos && !values.arquivo) {
        Modal.error({
          title: 'Erro ao enviar ação',
          content: 'É obrigatório o envio de um arquivo para prosseguir com a ação.',
        });
        return;
      }

      if (deveRequerUploadQuandoAceitar && !acao.requisitos.requerMultiplosArquivos && !values.arquivo) {
        Modal.error({
          title: 'Erro ao enviar ação',
          content: 'É obrigatório o envio de um arquivo ao aceitar esta ação.',
        });
        return;
      }

      if (deveRequerUploadQuandoRecusado && !acao.requisitos.requerMultiplosArquivos && !values.arquivo) {
        Modal.error({
          title: 'Erro ao enviar ação',
          content: 'É obrigatório o envio de um arquivo ao recusar esta ação.',
        });
        return;
      }


      executarAcaoFornecedor(formData)
        .then((response: any) => {
          if (response && response.valid) {
            onSuccess();
            onClose();
          }
        })
        .catch(err => {
          console.error(err);
        });
    };

    if (acao.requisitos.requerConfirmacao) {
      Modal.confirm({
        title: 'Confirmar ação',
        content: acao.descricaoConfirmacao,
        okText: 'Sim',
        cancelText: 'Não',
        onOk: executar,
      });
    } else {
      executar();
    }
  };

  return (
    <Modal title={acao.acao} visible={visible} width="650px" onCancel={onClose} footer={null}>
      <MasterDetailForm
        validationSchema={Yup.object().shape({
                            justificativa: isObjNotEmpty(acao) && acao.requisitos.requerJustificativa && ((acao.requisitos.aceitarOuRecusar && decisao === 'recusar') || !acao.requisitos.aceitarOuRecusar) ? Yup.string()
                              .required('justificativa é obrigátoria')
                              .max(300, 'A justificativa deve ter no máximo 300 caracteres.') : Yup.string().notRequired(),
                          })}
        onSubmit={handleSubmit}
        initialValues={{ sasCondicaoPagamentoId: (condicoesPagamento.find((c) =>  { return c.text === '30 DIAS FORA A SEMANA DE FATUR'; }) || { value: undefined }).value }}
        actions={[
          <Button type="link" onClick={onClose}>
            {readOnly ? 'Fechar' : 'Cancelar'}
          </Button>,
          !readOnly && <FormSubmitButton type="primary">Confirmar</FormSubmitButton>,
        ].filter(Boolean)}>
        <Widget>
          <Row >
            <Row style={{ marginBottom: 20 }}>{acao.descricao}</Row>

            {acao.requisitos.aceitarOuRecusar && (
              <Radio.Group onChange={e => setDecisao(e.target.value)} value={decisao} style={{ marginBottom: 20}}>
                <Radio value="aceitar">{acao.requisitos.textoAceitar || 'Aceitar'}</Radio>
                <Radio value="recusar">{acao.requisitos.textoRecusar || 'Recusar'}</Radio>
              </Radio.Group>
            )}

            {acao.requisitos.requerValor &&
              <Row style={{marginTop: 16}}>
                <CurrencyInput
                  name="valor"
                  label="Valor (R$)"
                  span={4}
                  required={decisao === 'aceitar'}
                  disabled={decisao === 'recusar'}
                />

                {acao.requisitos.requerCondicaoPagamento && (
                  <FormSelect
                    label="Condição de Pagamento"
                    name="sasCondicaoPagamentoId"
                    options={condicoesPagamento}
                    span={4}
                    disabled={true}
                  />
                )}
              </Row>

            }
            {acao.requisitos.requerMultiplosArquivos && (
              <Row style={{ marginBottom: 16}}>
                <Upload
                  multiple
                  fileList={fileList}
                  beforeUpload={(file) => {
                    const uploadFile = {
                      uid: file.uid || `${Date.now()}-${file.name}`,
                      name: file.name,
                      status: 'done' as const,
                      originFileObj: file,
                    };
                    setFileList([...fileList, uploadFile as any]);
                    return false;
                  }}
                  onRemove={(file) => {
                    setFileList(fileList.filter((f) => f.uid !== file.uid));
                  }}
                  accept=".xlsx, .xls, .png, .doc, .docx, .ppt, .pptx, .txt, .pdf"
                >
                  <Button type="primary" icon="ui-upload">
                    Adicionar Arquivos
                  </Button>
                </Upload>
              </Row>
            )}


            {acao.requisitos.visualizarDocumento && acao.documentoFornecedor && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 16,
                }}>
                <Tooltip title="Download Arquivo">
                  <Button
                    icon="ui-download"
                    type="link"
                    onClick={() => {
                      const isTemplate = acao.documentoFornecedor.id === '00000000-0000-0000-0000-000000000000';
                      if (isTemplate) {
                        downloadTemplateEsclarecimentoTecnico();
                      } else {
                        downloadArquivo(acao.documentoFornecedor.id, acao.documentoFornecedor.arquivoNomeDownload);
                      }
                    }}
                  />
                </Tooltip>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>{acao.documentoFornecedor.descricao}</span>
                </div>
              </div>
            )}

            {acao.tipoAcao === 'enviar_revisao_proposta_tecnica' && acao.justificativa && (
              <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, padding: 12, marginBottom: 16, backgroundColor: '#fafafa' }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>MRN:</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{acao.justificativa}</div>
              </div>
            )}

            {acao.tipoAcao === 'enviar_revisao_proposta_tecnica' && acao.tipoDocumentoId && (
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, padding: 12, backgroundColor: '#f0f2f5', borderRadius: 4, gap: 4 }}>
                <ModalExibicaoArquivos
                  propostaId={acao.id}
                  sasTipoDocumentoId={acao.tipoDocumentoId}
                  disabled={true}
                  hideExcluido={true}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontWeight: 600 }}>Proposta Técnica</span>
                  <span style={{ fontSize: 12, color: '#8c8c8c' }}>Clique no ícone para visualizar os arquivos</span>
                </div>
              </div>
            )}

            {acao.tipoAcao === 'negociar_minuta_contratual' && acao.tipoDocumentoId && (
              <div style={{ marginBottom: 12 }}>
                <ModalExibicaoArquivos
                  propostaId={acao.id}
                  sasTipoDocumentoId={acao.tipoDocumentoId}
                  disabled={true}
                  hideExcluido={true}
                /> <span className="minuta-titulo">Minuta Contratual</span>
              </div>
            )}

            {acao.tipoAcao === 'negociar_minuta_contratual' && mensagensMinuta.length > 0 &&(
              <><strong>Comunicação com o Comprador</strong>
              <Row style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', padding: 8, marginTop: 8,  }}>
                {mensagensMinuta.length > 0 && <ListMensagem mensagens={mensagensMinuta} />}
                </Row>
              </>
            )}

            {acao.requisitos.requerJustificativa && (
              <Visibility
                visible={
                  (acao.requisitos.aceitarOuRecusar && decisao === 'recusar') || !acao.requisitos.aceitarOuRecusar || acao.requisitos.RequerJustificativaQuandoAceitar
                }>
                <FormTextArea name="justificativa" label="Justificativa" placeholder='Justificativa' maxLength={300}/>
              </Visibility>
            )}

            

            {acao.requisitos.requerUpload && !acao.requisitos.requerMultiplosArquivos && (
                <UploadFile
                  nameFormData={'arquivo'}
                  label={'Adicionar Arquivo'}
                  type="primary"
                  idDoComponente={'file'}
                />
            )}


            {acao.requisitos.requerUploadQuandoAceitar &&
             !acao.requisitos.requerMultiplosArquivos &&
             acao.requisitos.aceitarOuRecusar &&
             decisao === 'aceitar' && (
              <UploadFile
                nameFormData={'arquivo'}
                label={'Adicionar Arquivo'}
                type="primary"
                idDoComponente={'file-aceito'}
              />
            )}

            {acao.requisitos.requerUploadQuandoRecusado &&
             !acao.requisitos.requerMultiplosArquivos &&
             acao.requisitos.aceitarOuRecusar &&
             decisao === 'recusar' && (
              <UploadFile
                nameFormData={'arquivo'}
                label={'Adicionar Arquivo'}
                type="primary"
                idDoComponente={'file-recusado'}
              />
            )}

          
          </Row>
          
        </Widget>
      </MasterDetailForm>
    </Modal>
  );
};

export default ModalAcaoFornecedor;
